/**
 * The UPI / Pix QR upsell payment a page started, kept across a reload.
 *
 * The customer pays in another app (UPI app, or the bank app with the copied
 * Pix code); the browser may unload the tab meanwhile and the Payment Form's
 * success event is lost. The upsell model remembers the order when the payment
 * starts and, when the page is shown again, asks check-purchased about the
 * upsell (see upsellApmPaymentRecheckRequested).
 */

import type { UpsellStatus } from "@features/helpers";

export type PendingApmUpsell = {
    upsellId: string;
    orderId: string;
    startedAt: number;
};

const PENDING_KEY = "sg_pending_apm_upsell";
const HANDLED_KEY = "sg_handled_upsell_order_ids";

/** A Pix QR lives an hour (UPI less). */
export const PENDING_APM_UPSELL_TTL_MS = 75 * 60 * 1000;

type KeyValueStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const sessionStore = (): KeyValueStorage | null => {
    try {
        return typeof window === "undefined" ? null : window.sessionStorage;
    } catch {
        return null;
    }
};

export const rememberPendingApmUpsell = (upsell: PendingApmUpsell, storage = sessionStore()): void => {
    if (!upsell.upsellId || !upsell.orderId) return;
    try {
        storage?.setItem(PENDING_KEY, JSON.stringify(upsell));
    } catch {
        // ignore: the success event is still the main path
    }
};

export const readPendingApmUpsell = (now = Date.now(), storage = sessionStore()): PendingApmUpsell | null => {
    try {
        const raw = storage?.getItem(PENDING_KEY);
        if (!raw) return null;
        const upsell = JSON.parse(raw) as Partial<PendingApmUpsell>;
        if (
            !upsell.upsellId ||
            !upsell.orderId ||
            typeof upsell.startedAt !== "number" ||
            now - upsell.startedAt > PENDING_APM_UPSELL_TTL_MS
        ) {
            storage?.removeItem(PENDING_KEY);
            return null;
        }
        return upsell as PendingApmUpsell;
    } catch {
        return null;
    }
};

/**
 * How long the upsell page waits on an order this tab started when the server
 * knows nothing of it yet: its first webhook may still be on the way, or the
 * button was clicked and nothing reached Solidgate.
 */
export const LOCAL_APM_UPSELL_GRACE_MS = 20_000;

/** The APM upsell order the upsell page waits on instead of offering the button. */
export type AwaitedApmUpsellOrder = {
    orderId: string;
    /** Since when the page waits on an order this tab started; null once the server reports it unpaid. */
    localSince: number | null;
    /**
     * The order's payment window (CPF form, QR, UPI request) is open on the
     * page: waited on for as long as it stays open, so that a decline Solidgate
     * reports meanwhile (a QR expired, or an order cancelled by hand) reaches
     * the page while the customer still looks at the window.
     */
    open?: boolean;
};

/** One check of an upsell, as the wait needs it. */
export type ApmUpsellCheck = {
    status: UpsellStatus;
    /** The order the backend records as `awaiting_payment`, if any. */
    awaitingOrderId: string | null;
    /** The order the upsell's status is about, if any. */
    paymentId: string | null;
    checkedAt: number;
};

/**
 * What the upsell page waits on after a check. An order the server reports
 * unpaid is waited out. One whose payment window is open on the page is waited
 * on until the server has a verdict on it (the window closing ends the wait
 * itself). One this tab started before a reload is waited on for a while even
 * when the server says nothing about it, unless it was paid or declined; on a
 * slow connection the paywall's button would otherwise come back before the
 * answer and let the customer start a second payment.
 */
export const resolveAwaitedApmUpsellOrder = (
    awaited: AwaitedApmUpsellOrder | null,
    check: ApmUpsellCheck
): AwaitedApmUpsellOrder | null => {
    if (check.awaitingOrderId !== null) return { orderId: check.awaitingOrderId, localSince: null };
    if (awaited === null || check.status === "Success" || check.paymentId === awaited.orderId) return null;
    if (awaited.open) return awaited;
    if (awaited.localSince != null && check.checkedAt - awaited.localSince < LOCAL_APM_UPSELL_GRACE_MS) {
        return awaited;
    }
    return null;
};

export const clearPendingApmUpsell = (storage = sessionStore()): void => {
    try {
        storage?.removeItem(PENDING_KEY);
    } catch {
        // ignore
    }
};

/**
 * True the first time an upsell order is reported paid. The SDK success path
 * (confirmUpsellUpiPaymentFx) and the re-check can both see the same payment;
 * only the first one moves the customer on.
 */
export const claimUpsellOrderSuccess = (orderId: string, storage = sessionStore()): boolean => {
    if (!orderId) return true;
    try {
        const handled = JSON.parse(storage?.getItem(HANDLED_KEY) ?? "[]") as string[];
        if (handled.includes(orderId)) return false;
        storage?.setItem(HANDLED_KEY, JSON.stringify([...handled.slice(-19), orderId]));
    } catch {
        // Without storage there is nothing to dedupe against.
    }
    return true;
};
